// @flow
/** @jsx jsx */

import { partial } from 'lodash';

import { PaymentMethodImageStrip } from '@zapier/payment';
import { jsx } from '@emotion/core';
import { Border, Screen } from '@zapier/theme';
import {
  Button,
  ButtonGroup,
  FlexLayout,
  Text,
} from '@zapier/common-components';
import {
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** Currently selected method */
  currentMethod?: PaymentSourceMethod,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod, hasSwitched: boolean) => void,
  /** Array of supported payment methods */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const sectionCss = {
  border: `${Border.size.default} dashed ${Border.color.default}`,
  borderRadius: Border.radius.default,
  padding: 15,
  textAlign: 'center',

  [`@media screen and ${Screen.queries.tabletPortraitPlus}`]: {
    padding: 40,
  },
};

const selectCss = {
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: 20,
  '> *': {
    minWidth: 300,
    width: '40%',
    [`@media screen and ${Screen.queries.phoneOnly}`]: {
      width: '100%',
    },
  },
};

const methodResolver = {
  [paymentSourceMethods.card]: {
    accepts: ['Visa', 'Mastercard', 'American Express', 'Discover'],
    addText: 'Add Card',
    sortOrder: 2,
  },
  [paymentSourceMethods.paypal]: {
    accepts: ['PayPal'],
    addText: 'Connect PayPal',
    sortOrder: 1,
  },
  [paymentSourceMethods.bank]: {
    accepts: ['Bank Transfer (ACH)'],
    addText: 'Add Bank Account (US Only)',
    sortOrder: 3,
  },
};

const joinWithLast = (
  list: Array<string>,
  joiner: string,
  last: string
): ?string => {
  if (list.length < 2) {
    return null;
  }
  const fullList = list.join(joiner);
  const lastJoin = fullList.lastIndexOf(joiner);
  return (
    fullList.substring(0, lastJoin) +
    last +
    fullList.substring(lastJoin + joiner.length)
  );
};

const NoPaymentInformation = (props: Props) => {
  const methods = [...props.supportedMethods]
    .map(method => method.method)
    .sort((a, b) => methodResolver[a].sortOrder - methodResolver[b].sortOrder);

  const acceptText = joinWithLast(
    methods.reduce((acc, m) => [...acc, ...methodResolver[m].accepts], []),
    ', ',
    ' or '
  );

  const buttons = methods.map(m => (
    <Button
      color="secondary"
      isMinWidth={true}
      key={m}
      label={methodResolver[m].addText}
      onClick={partial(props.onSelectSource, m, false)}
    >
      {methodResolver[m].addText}
    </Button>
  ));

  return (
    <section css={sectionCss}>
      <h3>
        <Text type="section-head">No Payment Info</Text>
      </h3>
      {acceptText && (
        <p>
          <Text type="body">We accept {acceptText}.</Text>
        </p>
      )}
      <FlexLayout justify="center">
        <PaymentMethodImageStrip methods={methods} />
      </FlexLayout>
      <div css={selectCss}>
        {props.supportedMethods.length < 3 ? (
          <ButtonGroup
            direction="row"
            isFlex={true}
            justify="center"
            margin="top"
            propsQueries={{ phoneOnly: { direction: 'column' } }}
          >
            {buttons}
          </ButtonGroup>
        ) : (
          props.supportedMethods.length && (
            <PaymentMethodSelect
              currentMethod={props.currentMethod}
              onChange={props.onSelectSource}
              supportedMethods={props.supportedMethods}
            />
          )
        )}
      </div>
    </section>
  );
};

export default NoPaymentInformation;
